/**
 * Scheduling when a pool opens for trading.
 *
 * This is an on-chain schedule, not a timer on anybody's computer. The CPMM
 * pool carries an `open_time`, and the cp-swap program refuses to swap until
 * the cluster clock passes it. Verified against Raydium's real devnet program
 * on a local validator:
 *
 *     Program log: Instruction: SwapBaseInput
 *     AnchorError thrown in programs/cp-swap/src/instructions/swap_base_input.rs:82.
 *     Error Code: NotApproved. Error Number: 6000. Error Message: Not approved.
 *     ... failed: custom program error: 0x1770
 *
 * and the same swap succeeding once the clock passed it. So nothing has to stay
 * running, no key sits anywhere waiting, and the schedule holds even if every
 * machine involved is switched off.
 *
 * What was NOT established: whether adding or removing liquidity is gated the
 * same way. Attempts before the open time failed for unrelated reasons (an
 * underfunded payer, an SDK account lookup), so this module claims only what
 * was observed — swaps.
 */

/** Seconds. A pool that opens the instant it is created. */
export const OPEN_IMMEDIATELY = 0;

/**
 * How far ahead a scheduled time has to be. The create-pool transaction still
 * has to be built, approved in a wallet, land and confirm; an open time two
 * seconds out is one the pool would miss, which reads to everyone watching as
 * the schedule not working.
 */
export const MIN_LEAD_SECONDS = 120;

/** Beyond this, warn. Not refused: a long pre-announcement is a real choice. */
export const FAR_FUTURE_SECONDS = 90 * 24 * 60 * 60;

export interface ScheduleProblem {
  fatal: boolean;
  message: string;
}

export interface ParsedSchedule {
  /** Unix seconds, or OPEN_IMMEDIATELY. */
  unix: number;
  problems: ScheduleProblem[];
}

/**
 * Turns a `datetime-local` value (which has no timezone and means "local wall
 * clock") into unix seconds.
 *
 * Returns null rather than a guess when the value is not a complete
 * date-and-time — a half-typed field should read as "not set yet", never as
 * some other moment.
 */
export function localInputToUnix(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return null;
  const ms = new Date(value).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

/** The inverse, for pre-filling the same control. */
export function unixToLocalInput(unix: number): string {
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Validates a requested open time against the current clock.
 *
 * `nowSec` is a parameter rather than read from the system clock so this is
 * testable and so a caller can pass the CLUSTER's clock, which is the one the
 * program compares against and can drift from the browser's.
 */
export function validateOpenTime(unix: number | null, nowSec: number): ParsedSchedule {
  const problems: ScheduleProblem[] = [];

  if (unix === null) {
    return {
      unix: OPEN_IMMEDIATELY,
      problems: [{ fatal: true, message: "Enter a complete date and time, or choose to open immediately." }],
    };
  }
  if (unix === OPEN_IMMEDIATELY) return { unix: OPEN_IMMEDIATELY, problems };

  if (!Number.isInteger(unix) || unix < 0) {
    problems.push({ fatal: true, message: "Open time must be a whole number of seconds since the epoch." });
    return { unix: OPEN_IMMEDIATELY, problems };
  }
  if (unix <= nowSec) {
    problems.push({
      fatal: true,
      message: "That time has already passed. Pick a future time, or open the pool immediately.",
    });
  } else if (unix - nowSec < MIN_LEAD_SECONDS) {
    problems.push({
      fatal: true,
      message:
        `Open time must be at least ${MIN_LEAD_SECONDS / 60} minutes ahead. The pool still has to be ` +
        "built, approved in your wallet, and confirmed before then.",
    });
  } else if (unix - nowSec > FAR_FUTURE_SECONDS) {
    problems.push({
      fatal: false,
      message:
        "That is more than 90 days out. The liquidity is locked in an untradeable pool until then, " +
        "and you cannot bring the date forward afterwards.",
    });
  }

  return { unix, problems };
}

/** "in 2h 14m", "in 45s", "open" — for a countdown. */
export function describeCountdown(openUnix: number, nowSec: number): string {
  if (openUnix === OPEN_IMMEDIATELY || openUnix <= nowSec) return "open";
  let remaining = openUnix - nowSec;
  const d = Math.floor(remaining / 86400);
  remaining -= d * 86400;
  const h = Math.floor(remaining / 3600);
  remaining -= h * 3600;
  const m = Math.floor(remaining / 60);
  const s = remaining - m * 60;

  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (d || h) parts.push(`${h}h`);
  if (d || h || m) parts.push(`${m}m`);
  if (!d && !h) parts.push(`${s}s`);
  return `in ${parts.join(" ")}`;
}

/**
 * Both clocks, always. A launch time is the one number where a timezone
 * misreading is expensive and completely silent.
 */
export function describeOpenTime(openUnix: number): string {
  if (openUnix === OPEN_IMMEDIATELY) return "immediately on creation";
  const d = new Date(openUnix * 1000);
  return `${d.toLocaleString()} local · ${d.toISOString().replace(".000", "")} · unix ${openUnix}`;
}

export function isOpen(openUnix: number, nowSec: number): boolean {
  return openUnix === OPEN_IMMEDIATELY || nowSec >= openUnix;
}
